import { Injectable, inject } from '@angular/core';

import { LoggerFactory } from '@frontend/sports/common/core/feature/logging';
import { GRID_ACTION_SCHEMA } from '@frontend/sports/grid/core/feature/store';
import { EventsService, NativeEventType } from '@frontend/vanilla/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, debounceTime, filter, takeUntil, tap } from 'rxjs';

import { StateConverterService } from './state-converter.service';
import { StoragePersister } from './storage-persister';
import { StorePersistActions } from './store-persist-actions';

@Injectable()
export class StorePersistEffects {
    private store = inject(Store);
    private actions = inject(Actions);
    private storagePersister = inject(StoragePersister);
    private stateConverter = inject(StateConverterService);
    private eventsService = inject(EventsService);

    private static readonly SAVE_STORAGE_BUFFER_TIME = 200; // save all save state requests which come in 200 milliseconds and then update the storage.
    private static readonly ExitLoyaltyMode = 'EXIT_LOYALTY_MODE';
    private readonly logger = inject(LoggerFactory).getLogger('StorePersistEffects');

    handleSaveStateEffect$ = createEffect(
        () => {
            return this.actions.pipe(
                filter((action) => !action.type.startsWith(GRID_ACTION_SCHEMA)),
                debounceTime(StorePersistEffects.SAVE_STORAGE_BUFFER_TIME),
                concatLatestFrom(() => this.store),
                tap(([_, currentState]) => this.saveState(currentState)),
                catchError((error, caught) => {
                    this.logger.error(error, 'Failed to save sports betslip state');

                    return caught;
                }),
                takeUntil(
                    this.eventsService.events.pipe(
                        filter((t) => t?.data?.eventName === StorePersistEffects.ExitLoyaltyMode || t.eventName === NativeEventType.RESET_TERMINAL),
                    ),
                ),
            );
        },
        { dispatch: false },
    );

    handleManualSaveStateEffect$ = createEffect(
        () => {
            return this.actions.pipe(
                ofType(StorePersistActions.saveState),
                concatLatestFrom(() => this.store),
                tap(([_, currentState]) => this.saveState(currentState)),
            );
        },
        { dispatch: false },
    );

    private saveState(currentState: object): void {
        const previousState = this.storagePersister.load() || {};
        const convertedState = this.stateConverter.convert(currentState);
        const newState = Object.assign({}, previousState, convertedState);
        this.storagePersister.save(newState);
    }
}
